/**
 * Description: Systems catalog for managing product-system relationships (inline component)
 */

import React, { useState, useEffect } from 'react'
import { Search, Trash2 } from 'lucide-react'
import { api, System } from '@services/api'
import { toast } from 'react-toastify'

interface SystemsCatalogProps {
  productId?: number
  productName: string
  onSystemsChange?: (systemIds: number[]) => void
  selectedSystemIds?: number[]
}

const SystemsCatalog: React.FC<SystemsCatalogProps> = ({
  productId,
  productName,
  onSystemsChange,
  selectedSystemIds = [],
}) => {
  const [allSystems, setAllSystems] = useState<System[]>([])
  const [linkedSystems, setLinkedSystems] = useState<System[]>([])
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>(selectedSystemIds)
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    loadSystems()
  }, [])

  const loadSystems = async () => {
    setIsLoading(true)
    try {
      const all = await api.getAllSystems()
      setAllSystems(all)

      // If we have a product ID, also load linked systems
      if (productId) {
        const linked = await api.getProductSystems(productId)
        setLinkedSystems(linked)
      }
    } catch (error) {
      toast.error('Failed to load systems')
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLinkSystem = async (systemId: number) => {
    const system = allSystems.find((s) => s.id === systemId)
    const systemName = system?.system_name || 'System'

    // Create mode: Update local state and notify parent
    if (!productId) {
      const newSelectedIds = [...localSelectedIds, systemId]
      setLocalSelectedIds(newSelectedIds)
      if (onSystemsChange) {
        onSystemsChange(newSelectedIds)
      }
      toast.success(`${systemName} selected`)
      return
    }

    // Edit mode: Call API to link
    try {
      await api.linkProductToSystem(systemId, productId)
      toast.success(`${systemName} linked successfully to ${productName}`)
      loadSystems()
    } catch (error) {
      toast.error(`Failed to link ${systemName}`)
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  const handleUnlinkSystem = async (systemId: number) => {
    const system = linkedSystems.find((s) => s.id === systemId) || allSystems.find((s) => s.id === systemId)
    const systemName = system?.system_name || 'System'

    // Create mode: Update local state and notify parent
    if (!productId) {
      const newSelectedIds = localSelectedIds.filter((id) => id !== systemId)
      setLocalSelectedIds(newSelectedIds)
      if (onSystemsChange) {
        onSystemsChange(newSelectedIds)
      }
      toast.success(`${systemName} removed`)
      return
    }

    // Edit mode: Call API to unlink
    try {
      await api.unlinkProductFromSystem(systemId, productId)
      toast.success(`${systemName} unlinked successfully from ${productName}`)
      loadSystems()
    } catch (error) {
      toast.error(`Failed to unlink ${systemName}`)
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  const isSystemLinked = (systemId: number) => {
    // In create mode, check local selected IDs
    if (!productId) {
      return localSelectedIds.includes(systemId)
    }
    // In edit mode, check linked systems from API
    return linkedSystems.some((s) => s.id === systemId)
  }

  // Get display list for linked/selected systems
  const displayLinkedSystems = productId
    ? linkedSystems
    : allSystems.filter((s) => s.id && localSelectedIds.includes(s.id))

  const filteredSystems = allSystems
    .filter(
      (system) =>
        system.system_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        system.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        system.business_owner?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => a.system_name.localeCompare(b.system_name))

  return (
    <div className="space-y-6">
      {/* Linked Systems Section */}
      <div>
        <h3 className="mb-3 text-lg font-medium text-gray-900 dark:text-gray-100">
          {productId ? 'Linked Systems' : 'Selected Systems'} ({displayLinkedSystems.length})
        </h3>
        {displayLinkedSystems.length > 0 ? (
          <div className="space-y-2">
            {displayLinkedSystems
              .sort((a: System, b: System) => a.system_name.localeCompare(b.system_name))
              .map((system: System) => (
                <div
                  key={system.id}
                  className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800/50 dark:bg-green-950/30"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{system.system_name}</p>
                    {system.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{system.description}</p>
                    )}
                    <div className="mt-1 flex gap-2">
                      {system.management_type && (
                        <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                          {system.management_type}
                        </span>
                      )}
                      {system.business_owner && (
                        <span className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                          Owner: {system.business_owner}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => system.id && handleUnlinkSystem(system.id)}
                    className="ml-4 cursor-pointer rounded p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                    title="Unlink system"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {productId ? 'No systems linked to this product yet.' : 'No systems selected yet.'}
          </p>
        )}
      </div>

      {/* Available Systems Section */}
      <div>
        <h3 className="mb-3 text-lg font-medium text-gray-900 dark:text-gray-100">Available Systems</h3>

        {/* Search */}
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
              placeholder="Search systems..."
            />
          </div>
        </div>

        {/* Systems List */}
        {isLoading ? (
          <p className="py-4 text-center text-gray-500 dark:text-gray-400">Loading systems...</p>
        ) : (
          <div className="space-y-2">
            {filteredSystems
              .filter((s) => s.id && !isSystemLinked(s.id))
              .map((system) => (
                <div
                  key={system.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 hover:bg-gray-100 dark:border-gray-700 dark:bg-white/5 dark:hover:bg-gray-700"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{system.system_name}</p>
                    {system.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{system.description}</p>
                    )}
                    <div className="mt-1 flex gap-2">
                      {system.management_type && (
                        <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                          {system.management_type}
                        </span>
                      )}
                      {system.business_owner && (
                        <span className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                          Owner: {system.business_owner}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => system.id && handleLinkSystem(system.id)}
                    className="bg-primary-600 hover:bg-primary-700 ml-4 cursor-pointer rounded px-4 py-2 text-white"
                  >
                    Link
                  </button>
                </div>
              ))}
            {filteredSystems.filter((s) => s.id && !isSystemLinked(s.id)).length === 0 && (
              <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No systems found matching your search.' : 'All systems are already linked.'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SystemsCatalog
